import React, { useId } from 'react';
import {
    Box,
    HStack,
    Icon,
    IconButton,
    IconButtonProps,
    PlacementWithLogical,
} from '@chakra-ui/react';
import { FiCopy } from 'react-icons/fi';
import useCopyToClipboard from '../hooks/useCopyToClipboard';
import CopyTooltip from './CopyTooltip';

type ChakraIconProp = React.ComponentProps<typeof Icon>['as'];

interface CopyButtonProps extends Omit<IconButtonProps, 'aria-label' | 'icon' | 'onClick'> {
    /** Text to copy to the clipboard. */
    text: string;
    /** Accessible label for the button. Defaults to "Copy". */
    label?: string;
    /** Icon component to render. Defaults to FiCopy. */
    iconAs?: ChakraIconProp;
    /** Pixel size of the rendered icon. Defaults to 3 (Chakra spacing). */
    iconSize?: number;
    /** Tooltip placement relative to `tooltipAnchor` (or the icon if omitted). Defaults to "top". */
    placement?: PlacementWithLogical;
    /** Whether to call event.stopPropagation() on click. Defaults to true. */
    stopPropagation?: boolean;
    /** Optional callback fired after a successful copy. */
    onCopy?: () => void;
    /** Visible text to anchor the "Copied!" bubble to. When omitted, the bubble anchors to the icon. */
    tooltipAnchor?: React.ReactElement;
}

/**
 * Standard copy-to-clipboard icon button used across the platform.
 * Shows a small "Copied!" tooltip on success instead of a global toast.
 */
const CopyButton: React.FC<CopyButtonProps> = ({
    text,
    label = 'Copy',
    iconAs,
    iconSize = 3,
    placement = 'top',
    stopPropagation = true,
    onCopy,
    tooltipAnchor,
    size = 'xs',
    variant = 'ghost',
    color = 'gray.400',
    _hover = { color: 'gray.600' },
    ...rest
}) => {
    const copyKey = useId();
    const { copy, isCopied } = useCopyToClipboard();

    const handleClick = async (e: React.MouseEvent) => {
        if (stopPropagation) e.stopPropagation();
        const ok = await copy(text, copyKey, { x: e.clientX, y: e.clientY });
        if (ok) onCopy?.();
    };

    const icon = (
        <IconButton
            aria-label={label}
            icon={
                <Icon
                    as={(iconAs ?? (FiCopy as unknown as ChakraIconProp))}
                    w={iconSize}
                    h={iconSize}
                />
            }
            size={size}
            variant={variant}
            color={color}
            _hover={_hover}
            onClick={handleClick}
            {...rest}
        />
    );

    if (tooltipAnchor) {
        return (
            <CopyTooltip isCopied={isCopied(copyKey)} placement={placement}>
                <HStack spacing={1} display="inline-flex" alignItems="center">
                    <Box as="span" display="inline-block" lineHeight="normal">
                        {tooltipAnchor}
                    </Box>
                    {icon}
                </HStack>
            </CopyTooltip>
        );
    }

    return (
        <CopyTooltip isCopied={isCopied(copyKey)} placement={placement}>
            {icon}
        </CopyTooltip>
    );
};

export default CopyButton;
